import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, timer, of, Subject } from 'rxjs';
import {
  switchMap,
  catchError,
  retry,
  takeUntil,
  tap,
  shareReplay
} from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  ServerStatusResponse,
  SimpleHealthResponse,
  DetailedHealthResponse,
  ServerHealthStatus
} from '../Models/server-status.model';

/**
 * Service for monitoring backend server status and health.
 *
 * Provides methods to:
 * - Get full server diagnostics (requires CanManageUserRoles)
 * - Simple health check (anonymous, for monitoring)
 * - Detailed health check (requires CanManageUserRoles)
 * - Poll health status at intervals
 *
 * @see src/app/Documentation/ServerStatus/ServerStatusApi.md
 */
@Injectable({ providedIn: 'root' })
export class ServerStatusService {
  private readonly baseUrl = `${environment.apiRoot}serverstatus`;

  /** Default polling interval in milliseconds */
  private readonly DEFAULT_POLL_INTERVAL = 60000; // 60 seconds

  /** Retry configuration */
  private readonly RETRY_COUNT = 2;
  private readonly RETRY_DELAY = 1000; // 1 second

  constructor(private http: HttpClient) {}

  /**
   * Get full server status including all diagnostics.
   *
   * **Authorization**: Requires `CanManageUserRoles` permission
   *
   * @returns Observable<ServerStatusResponse> Full server status
   * @throws 401 if not authenticated
   * @throws 403 if lacking permission
   * @throws 503 if server is unhealthy
   */
  getFullStatus(): Observable<ServerStatusResponse> {
    return this.http.get<ServerStatusResponse>(this.baseUrl).pipe(
      retry({ count: this.RETRY_COUNT, delay: this.RETRY_DELAY })
    );
  }

  /**
   * Simple health check for monitoring.
   *
   * **Authorization**: None (anonymous access allowed)
   *
   * Use this endpoint for:
   * - Load balancer health checks
   * - External monitoring tools
   * - Kubernetes liveness probes
   *
   * @returns Observable<SimpleHealthResponse> Simple health status
   */
  getHealthCheck(): Observable<SimpleHealthResponse> {
    return this.http.get<SimpleHealthResponse>(`${this.baseUrl}/health`);
  }

  /**
   * Detailed health check with component-level status.
   *
   * **Authorization**: Requires `CanManageUserRoles` permission
   *
   * @returns Observable<DetailedHealthResponse> Detailed component health
   * @throws 401 if not authenticated
   * @throws 403 if lacking permission
   */
  getDetailedHealth(): Observable<DetailedHealthResponse> {
    return this.http.get<DetailedHealthResponse>(`${this.baseUrl}/health/detailed`).pipe(
      retry({ count: this.RETRY_COUNT, delay: this.RETRY_DELAY })
    );
  }

  /**
   * Poll health status at specified interval.
   *
   * Automatically handles errors by returning 'Unknown' status.
   * The polling continues even after errors.
   *
   * @param intervalMs Polling interval in milliseconds (default: 60000)
   * @returns Observable<SimpleHealthResponse> Emits health status at each interval
   *
   * @example
   * ```typescript
   * // In component
   * this.serverStatusService.pollHealthStatus(30000)
   *   .pipe(takeUntil(this.destroy$))
   *   .subscribe(status => {
   *     this.currentStatus = status;
   *   });
   * ```
   */
  pollHealthStatus(intervalMs: number = this.DEFAULT_POLL_INTERVAL): Observable<SimpleHealthResponse> {
    return timer(0, intervalMs).pipe(
      switchMap(() =>
        this.getHealthCheck().pipe(
          catchError(() =>
            of({
              status: 'Unknown' as ServerHealthStatus,
              timestamp: new Date().toISOString()
            })
          )
        )
      )
    );
  }

  /**
   * Check if the server is healthy (simple boolean check).
   *
   * @returns Observable<boolean> True if server is healthy
   */
  isHealthy(): Observable<boolean> {
    return this.getHealthCheck().pipe(
      switchMap(response => of(response.status === 'Healthy')),
      catchError(() => of(false))
    );
  }

  /**
   * Get status color class based on health status.
   * Useful for applying consistent styling across components.
   *
   * @param status Server health status
   * @returns CSS class name for the status
   */
  getStatusColorClass(status: ServerHealthStatus): string {
    switch (status) {
      case 'Healthy':
        return 'status-healthy';
      case 'Degraded':
        return 'status-degraded';
      case 'Unhealthy':
      case 'Error':
        return 'status-error';
      default:
        return 'status-unknown';
    }
  }

  /**
   * Get Material icon name for the status.
   *
   * @param status Server health status
   * @returns Material icon name
   */
  getStatusIcon(status: ServerHealthStatus): string {
    switch (status) {
      case 'Healthy':
        return 'check_circle';
      case 'Degraded':
        return 'warning';
      case 'Unhealthy':
      case 'Error':
        return 'error';
      default:
        return 'help_outline';
    }
  }

  /**
   * Parse uptime string to human-readable format.
   * Converts "5.12:30:45" to "5 days, 12 hours, 30 minutes"
   *
   * @param uptime Uptime string in TimeSpan format (d.hh:mm:ss)
   * @returns Human-readable uptime string
   */
  parseUptime(uptime: string): { days: number; hours: number; minutes: number; seconds: number } {
    // Format: "d.hh:mm:ss" or "hh:mm:ss"
    const parts = uptime.split('.');
    let days = 0;
    let timePart = uptime;

    if (parts.length === 2) {
      days = parseInt(parts[0], 10);
      timePart = parts[1];
    }

    const timeParts = timePart.split(':');
    const hours = parseInt(timeParts[0], 10) || 0;
    const minutes = parseInt(timeParts[1], 10) || 0;
    const seconds = parseInt(timeParts[2], 10) || 0;

    return { days, hours, minutes, seconds };
  }
}
